import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  Stack,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Collapse,
  Spinner,
  useToast,
  CheckboxGroup,
  Checkbox,
} from "@chakra-ui/react";
import DataGrid from "react-data-grid";
//import "react-data-grid/lib/styles.css";

import Papa from "papaparse";
import axios from "axios";

import ForecastCard from "./cards/ForecastCard";
import { FaBrain, FaCalculator, FaTrashRestore, FaUpload } from "react-icons/fa";
import TargetSelectionModal from "../modal/TargetSelectionModal";

const AnalyticModelChoosing = () => {
  // 1. CSV & DataGrid states
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileUploaded, setFileUploaded] = useState(false);

  // 2. Target and model results states
  const [selectedTarget, setSelectedTarget] = useState("");
  const [predictionResults, setPredictionResults] = useState(null);
  const [forecastResults, setForecastResults] = useState(null);

  // 3. Header detection & mapping (if double-header CSV)
  const [headerNumber, setHeaderNumber] = useState(1);
  const [keyMapping, setKeyMapping] = useState({});

  // 4. "Baseline" aggregator for each predictor & target
  const [currentPredictorValues, setCurrentPredictorValues] = useState({});
  const [targetCurrentValue, setTargetCurrentValue] = useState(null);

  // 5. Forecast & testing config
  const [forecastSteps, setForecastSteps] = useState(5);
  const [forecastModel, setForecastModel] = useState("arima");
  const [predictionOpen, setPredictionOpen] = useState(true);

  // 6. Single-predictor adjustments
  const [selectedAdjustmentPercent, setSelectedAdjustmentPercent] = useState(10);
  const [selectedAdjustmentDirection, setSelectedAdjustmentDirection] =
    useState("decrease");
  const [selectedPredictorForAdjustment, setSelectedPredictorForAdjustment] =
    useState("");
  const [singlePredictorAdjustment, setSinglePredictorAdjustment] = useState(null);

  // 7. Loading states for API calls
  const [isPredictLoading, setIsPredictLoading] = useState(false);
  const [isForecastLoading, setIsForecastLoading] = useState(false);
  const [baselineYear, setBaselineYear] = useState(null);
  const [selectedSourceVariables, setSelectedSourceVariables] = useState([]);
  const toast = useToast();

  // keep file in state until they confirm
  const [fileToProcess, setFileToProcess] = useState(null);

  // all headers we found in the CSV
  const [previewCols, setPreviewCols] = useState([]);

  // which columns the user wants to skip
  const [excludedCols, setExcludedCols] = useState([]);

  // 8. Refs & modals
  const fileInputRef = useRef(null);
  const {
    isOpen: isTargetOpen,
    onOpen: openTargetModal,
    onClose: closeTargetModal,
  } = useDisclosure();

  // for your skip-columns modal
  const {
    isOpen: isSkipOpen,
    onOpen: openSkipModal,
    onClose: closeSkipModal,
  } = useDisclosure();

  const {
    isOpen: forecastIsOpen,
    onOpen: forecastOnOpen,
    onClose: forecastOnClose,
  } = useDisclosure(); // Forecast modal

  // New state: toggle for the DataGrid collapsible card
  const [showDataGrid, setShowDataGrid] = useState(false);

  // Helper: check if a row is entirely non-numeric
  const isRowNonNumeric = (row) =>
    row.every((cell) => {
      const trimmed = cell.toString().trim();
      return trimmed === "" || isNaN(trimmed);
    });

  const validateCSV = (fields, data) => {
    const errors = [];
    const lowerFields = fields.map((f) => f.toLowerCase());
    // require year…
    const hasYear =
      lowerFields.includes("year") ||
      lowerFields.some((f) => f.includes("ปี"));
    if (!hasYear) errors.push("CSV ต้องมีคอลัมน์ปี (year)");

    data.forEach((row, rowIndex) => {
      fields.forEach((field) => {
        if (excludedCols.includes(field)) return; // **skip**
        const raw = row[field];
        if (raw == null || raw === "") return;
        const key = field.toLowerCase();

        if (key === "gender") {
          const v = raw.toString().trim().toUpperCase();
          if (!["M", "F", "1", "0"].includes(v)) {
            errors.push(
              `Row ${rowIndex + 1} คอลัมน์ "gender" ต้องเป็น 'M','F','1' หรือ '0' (พบ "${raw}")`
            );
          }
          return;
        }
        if (key === "year" || key.includes("ปี")) return;
        if (isNaN(raw)) {
          errors.push(
            `Row ${rowIndex + 1} คอลัมน์ "${field}" ต้องเป็นตัวเลข (พบ "${raw}")`
          );
        }
      });
    });
    return errors;
  };

  // Step 1: light parse to get headers, then open modal
  const handleFileSelected = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileToProcess(file);
    Papa.parse(file, {
      header: true,
      preview: 1,
      skipEmptyLines: true,
      complete: ({ meta }) => {
        setPreviewCols(meta.fields || []);
        setExcludedCols([]); // reset previous
        openSkipModal();
      },
      error: (err) => {
        toast({
          title: "ไม่สามารถอ่านหัวตารางได้",
          description: err.message,
          status: "error",
          isClosable: true,
        });
      },
    });
  };

  // Step 2: user confirms which to skip → do full parse + validate + load grid
  const handleModalConfirm = () => {
    closeSkipModal();
    Papa.parse(fileToProcess, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data, meta }) => {
        const fields = (meta.fields || []).filter(
          (f) => !excludedCols.includes(f)
        );
        const errors = validateCSV(fields, data);
        if (errors.length) {
          toast({
            title: `พบ ${errors.length} ข้อผิดพลาด`,
            description: errors.slice(0, 5).join("\n") +
              (errors.length > 5
                ? `\n…and ยังมีอีก ${errors.length - 5} ข้อ`
                : ""),
            status: "error",
            isClosable: true,
            duration: 8000,
            position: "top",
          });
          // reset
          setColumns([]);
          setRows([]);
          setFileUploaded(false);
          return;
        }
        // build your grid columns & rows without the excluded
        setColumns(
          fields.map((f) => ({
            key: f,
            name: f,
            resizable: true,
            sortable: true,
          }))
        );
        setRows(
          data.map((r) =>
            Object.fromEntries(
              Object.entries(r).filter(([k]) => !excludedCols.includes(k))
            )
          )
        );
        setFileUploaded(true);
      },
      error: (err) => {
        toast({
          title: "Error parsing CSV",
          description: err.message,
          status: "error",
          isClosable: true,
        });
      },
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 1. Double-header detection (unchanged)
    Papa.parse(file, {
      preview: 2,
      header: false,
      skipEmptyLines: true,
      complete: (preview) => {
        let detectedHeaderNumber = 1;
        if (preview.data.length >= 2) {
          const [row0, row1] = preview.data;
          if (row0.every(cell => isNaN(cell)) && row1.every(cell => isNaN(cell))) {
            detectedHeaderNumber = 2;
            const mapping = {};
            row0.forEach((label, i) => mapping[label] = row1[i]);
            setKeyMapping(mapping);
          }
        }
        setHeaderNumber(detectedHeaderNumber);
      },
      error: () => setHeaderNumber(1),
    });

    // 2. Full parse + single-toast summary
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data, meta }) => {
        const fields = meta.fields || [];
        const errors = validateCSV(fields, data);

        if (errors.length) {
          // only show first 5 errors, mention if more
          const maxShow = 5;
          const list = errors
            .slice(0, maxShow)
            .map((msg, i) => `${i + 1}. ${msg}`)
            .join("\n");
          const moreNotice =
            errors.length > maxShow
              ? `\n…and ยังมีอีก ${errors.length - maxShow} ข้อผิดพลาด`
              : "";

          toast({
            title: `พบ ${errors.length} ข้อผิดพลาด`,
            description: list + moreNotice,
            status: "error",
            isClosable: true,
            duration: 10000,
            position: "top",
          });

          handleClear();
          return;
        }

        // 3. If valid, load into grid
        const newColumns = fields.map((f) => ({
          key: f,
          name: f,
          resizable: true,
          sortable: true,
        }));
        setColumns(newColumns);
        setRows(data);
        setFileUploaded(true);
      },
      error: (err) => {
        toast({
          title: "Error parsing CSV",
          description: err.message,
          status: "error",
          isClosable: true,
        });
      },
    });
  };

  //
  // B. Clear everything
  //
  /*const handleClear = () => {
    setColumns([]);
    setRows([]);
    setFileUploaded(false);
    setSelectedTarget("");
    setPredictionResults(null);
    setForecastResults(null);
    setSinglePredictorAdjustment(null);
    setSelectedPredictorForAdjustment("");
    setKeyMapping({});
    setCurrentPredictorValues({});
    setTargetCurrentValue(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };*/

  const handleClear = () => {
    // 1) Reset all your grid/API state
    setColumns([]);
    setRows([]);
    setFileUploaded(false);
    setSelectedTarget("");
    setPredictionResults(null);
    setForecastResults(null);
    setSinglePredictorAdjustment(null);
    setSelectedPredictorForAdjustment("");
    setKeyMapping({});
    setCurrentPredictorValues({});
    setTargetCurrentValue(null);
    setSelectedSourceVariables([]);

    // 2) Clear the two file inputs
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    const skipInput = document.getElementById("csv-input");
    if (skipInput) {
      skipInput.value = "";
    }

    // 3) Clear the File object and preview state
    setFileToProcess(null);
    setPreviewCols([]);
    setExcludedCols([]);
  };


  //
  // C. Once target is selected & CSV loaded, find baseline by aggregating all rows for the LATEST year
  //
  useEffect(() => {
    if (!fileUploaded || !selectedTarget || rows.length === 0) return;

    const yearCol = columns.find(
      (col) => col.key.toLowerCase() === "year" || col.key.includes("ปี")
    );

    if (!yearCol) {
      // If no year column, use the first row as baseline
      const row = rows[0];
      if (row) {
        const baselinePredictors = {};
        Object.keys(row).forEach((k) => {
          baselinePredictors[k] = parseFloat(row[k]) || 0;
        });
        setCurrentPredictorValues(baselinePredictors);
        setTargetCurrentValue(baselinePredictors[selectedTarget] || null);
        setBaselineYear("N/A");
      }
      return;
    }

    // Determine the maximum year (baseline year)
    let maxYear = -Infinity;
    rows.forEach((r) => {
      const y = parseFloat(r[yearCol.key]);
      if (!isNaN(y) && y > maxYear) {
        maxYear = y;
      }
    });
    if (maxYear === -Infinity) return;
    setBaselineYear(maxYear);

    // Filter all rows that match the maximum year
    const latestYearRows = rows.filter((r) => {
      const y = parseFloat(r[yearCol.key]);
      return !isNaN(y) && y === maxYear;
    });

    // Sum up each predictor's values across those rows
    if (latestYearRows.length > 0) {
      const baselinePredictors = {};
      columns.forEach((col) => {
        baselinePredictors[col.key] = 0;
      });
      latestYearRows.forEach((r) => {
        columns.forEach((col) => {
          const val = parseFloat(r[col.key]);
          if (!isNaN(val)) {
            baselinePredictors[col.key] += val;
          }
        });
      });
      setCurrentPredictorValues(baselinePredictors);
      setTargetCurrentValue(baselinePredictors[selectedTarget] || null);
    }
  }, [selectedTarget, fileUploaded, rows, columns]);

  //
  // D. Helper message for the target modal
  //
  const predictionMessage = () => {
    if (!fileUploaded || rows.length === 0 || !selectedTarget) return "";
    const yearColumnKey = columns.find(
      (col) =>
        col.key.toLowerCase() === "year" ||
        col.key.toLowerCase().includes("ปี")
    )?.key;
    if (!yearColumnKey) return "";
    const yearValues = rows.map((row) => row[yearColumnKey]).filter(Boolean);
    if (yearValues.length === 0) return "";
    const numericYears = yearValues
      .map((y) => parseInt(y, 10))
      .filter((y) => !isNaN(y));
    if (numericYears.length === 0) return "";
    const maxYear = Math.max(...numericYears);
    const previousYears = Array.from(new Set(numericYears))
      .filter((year) => year !== maxYear)
      .sort((a, b) => a - b);
    const targetLabel =
      columns.find((col) => col.key === selectedTarget)?.name ||
      selectedTarget;
    return `ระบบจะทำการเปรียบเทียบค่าความคลาดเคลื่อน (mean squared error) ของ model ต่างๆ จากการทำนายผล <b>${targetLabel}</b> โดยเทียบข้อมูลจริงที่ได้จากปี <b>${maxYear}</b> กับข้อมูล (ของปี <b>${maxYear}</b>) ที่ได้จากการประมาณการจากปีก่อนหน้า (ปี <b>${previousYears.join(
      ", "
    )}</b>)`;
  };

  const handleConfirmTarget = async () => {
    const file = fileToProcess;
    if (!selectedTarget || !file) {
      console.error("Missing selected target or file");
      toast({
        title: "ไม่พบไฟล์หรือยังไม่ได้เลือกตัวแปรเป้าหมาย",
        status: "error",
        isClosable: true,
      });
      return;
    }

    setIsPredictLoading(true);

    // Map display name back to real field if two‐row header
    let targetToSend = selectedTarget;
    if (headerNumber === 2 && keyMapping[selectedTarget]) {
      targetToSend = keyMapping[selectedTarget];
    }

    // Build list of source_vars:
    // 1) start from the checkboxes in the Target modal
    // 2) drop the target itself
    // 3) drop any columns the user skipped
    const sourceVars = selectedSourceVariables
      .filter(col => col !== targetToSend)
      .filter(col => !excludedCols.includes(col));

    const formData = new FormData();
    formData.append("file", file);
    formData.append("target", targetToSend);
    formData.append("header_number", headerNumber);
    formData.append("source_vars", sourceVars.join(","));

    console.log(`🔍 Predict → target: ${targetToSend}`);
    console.log(`🔍 Predict → source_vars: ${sourceVars.join(",")}`);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_MOUTH_API}/predict`, formData,
        { 
          headers: { 
            "Content-Type": "multipart/form-data",
            "X-API-KEY": `${process.env.REACT_APP_MOUTH_API_KEY}`
          } 
        }
      );
      setPredictionResults(response.data);
    } catch (error) {
      console.error("Error fetching predictions:", error);
      toast({
        title: "เกิดข้อผิดพลาดขณะเรียก API ทำนายผล",
        description: error.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsPredictLoading(false);
    }
  };

  // Automatically trigger prediction when the target changes and a file is uploaded.
  useEffect(() => {
    if (fileUploaded && selectedTarget) {
      handleConfirmTarget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTarget, fileUploaded]);

  const handleConfirmForecast = async () => {
    const file = fileToProcess;
    if (!selectedTarget || !file) {
      console.error("Missing selected target or file");
      return;
    }
    setIsForecastLoading(true);

    let targetToSend = selectedTarget;
    if (headerNumber === 2 && keyMapping[selectedTarget]) {
      targetToSend = keyMapping[selectedTarget];
    }

    // 1) Determine which columns to use as exogenous (exclude your `excludedCols`)
    const allCols = columns.map(c => c.key);
    const exogVars = allCols
      .filter(k => k !== selectedTarget)    // never include the target itself  
      .filter(k => k !== 'year' && !k.includes('ปี')) // drop year  
      .filter(k => !excludedCols.includes(k));       // drop whatever user skipped

    console.log(`exogVars --> ${JSON.stringify(exogVars)}`)

    // 2) Build formData
    const formData = new FormData();
    formData.append("file", file);
    formData.append("target", targetToSend);
    formData.append("header_number", headerNumber);
    formData.append("forecast_steps", forecastSteps);
    formData.append("model", forecastModel);
    formData.append("source_vars", exogVars.join(","));  // NEW

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_MOUTH_API}/forecast`,
        formData,
        { 
          headers: { 
            "Content-Type": "multipart/form-data",
            "X-API-KEY": `${process.env.REACT_APP_MOUTH_API_KEY}`
          } 
        }
      );

      console.log(`response.data -> ${JSON.stringify(response.data.impact_analysis.impacts)}`)
      setForecastResults(response.data);
      forecastOnClose();
    } catch (error) {
      console.error("Error fetching forecast:", error);
    } finally {
      setIsForecastLoading(false);
    }
  };

  //
  // G. Compute single‐predictor adjustment for the selected predictor
  //
  const handleComputeSinglePredictorAdjustment = () => {
    if (!forecastResults || !forecastResults.impact_analysis) {
      console.error("No impact analysis available from /forecast");
      return;
    }
    if (!targetCurrentValue) {
      console.error("No current target value found.");
      return;
    }
    if (!selectedPredictorForAdjustment) {
      console.error("No predictor selected.");
      return;
    }

    const desiredPercent = selectedAdjustmentPercent / 100;
    const sign = selectedAdjustmentDirection === "decrease" ? -1 : 1;
    const requiredTargetChange = targetCurrentValue * desiredPercent * sign;
    const impacts = forecastResults.impact_analysis.impacts;

    console.log(`impacts --> ${JSON.stringify(impacts)}`)

    const coef = parseFloat(impacts[selectedPredictorForAdjustment]);
    if (isNaN(coef) || coef === 0) {
      setSinglePredictorAdjustment({
        predictor: selectedPredictorForAdjustment,
        coefficient: coef,
        rawAdjustment: null,
        newCount: null,
        percentAdjustment: null,
      });
      return;
    }
    const rawChange = requiredTargetChange / coef;
    const baseline = currentPredictorValues[selectedPredictorForAdjustment] || 0;
    const newCount = baseline + rawChange;
    let percentAdjustment = null;
    if (baseline !== 0) {
      percentAdjustment = (rawChange / baseline) * 100;
    }
    setSinglePredictorAdjustment({
      predictor: selectedPredictorForAdjustment,
      coefficient: coef,
      rawAdjustment: rawChange,
      newCount,
      percentAdjustment,
    });
  };

  //
  // H. Render the single predictor result (if any)
  //
  const renderSinglePredictorAdjustmentResult = () => {
    if (!singlePredictorAdjustment) return null;

    const { predictor, coefficient, rawAdjustment, newCount, percentAdjustment } =
      singlePredictorAdjustment;

    const rawAdjStr = rawAdjustment != null ? rawAdjustment.toFixed(2) : "N/A";
    const newCtStr = newCount != null ? newCount.toFixed(2) : "N/A";
    const coefStr = coefficient != null ? coefficient.toFixed(3) : "N/A";
    const pctAdjStr =
      percentAdjustment != null ? percentAdjustment.toFixed(2) + "%" : "N/A";
    const baselineValue =
      currentPredictorValues[predictor] !== undefined
        ? currentPredictorValues[predictor].toFixed(2)
        : "N/A";
    const baselineYrStr = baselineYear ? baselineYear : "N/A";

    return (
      <Box
        mt={4}
        p={4}
        borderWidth="1px"
        borderColor="gray.800"
        borderRadius="md"
        boxShadow="md"
      >
        <Text fontWeight="bold" mb={2}>
          Predictor Adjustment for "{predictor}"
        </Text>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Predictor</Th>
              <Th>Baseline Value</Th>
              <Th>Baseline Year</Th>
              <Th>Coefficient</Th>
              <Th>Raw Adjustment</Th>
              <Th>New Count</Th>
              <Th>% Change (from baseline)</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>{predictor}</Td>
              <Td>{baselineValue}</Td>
              <Td>{baselineYrStr}</Td>
              <Td>{coefStr}</Td>
              <Td>{rawAdjStr}</Td>
              <Td>{newCtStr}</Td>
              <Td>{pctAdjStr}</Td>
            </Tr>
          </Tbody>
        </Table>
        <Box mt={2}>
          <Text fontSize="sm" fontWeight="bold">
            คำอธิบายคอลัมน์:
          </Text>
          <Text fontSize="sm">
            • <b>Predictor:</b> ตัวแปรที่ใช้ในการทำนายที่มีผลต่อเป้าหมาย
          </Text>
          <Text fontSize="sm">
            • <b>Baseline Value:</b> ค่าพื้นฐานของตัวแปร ณ ปีที่มีข้อมูลล่าสุด
          </Text>
          <Text fontSize="sm">
            • <b>Baseline Year:</b> ปีที่ใช้เป็นค่าพื้นฐานสำหรับการคำนวณ
          </Text>
          <Text fontSize="sm">
            • <b>Coefficient:</b> ค่าสัมประสิทธิ์ที่บ่งบอกความสัมพันธ์ระหว่างตัวแปรกับเป้าหมาย
          </Text>
          <Text fontSize="sm">
            • <b>Raw Adjustment:</b> การปรับเปลี่ยนค่าที่คำนวณได้โดยตรงจากค่าสัมประสิทธิ์
          </Text>
          <Text fontSize="sm">
            • <b>New Count:</b> ค่าที่ได้หลังจากการปรับเปลี่ยนค่าพื้นฐาน
          </Text>
          <Text fontSize="sm">
            • <b>% Change (from baseline):</b> เปอร์เซ็นต์การเปลี่ยนแปลงจากค่าพื้นฐาน
          </Text>
        </Box>
      </Box>
    );
  };

  return (
    <Flex direction="row" p={4} gap={4} w="100%">
      <Box w="100%">
        <Flex gap={2} mb={4} justify="space-between">
          <Flex gap={2}>
            {/*<Button
              leftIcon={<FaUpload />}
              variant="outline"
              colorScheme="teal"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              isDisabled={fileUploaded}
            >
              อัพโหลด CSV ไฟล์
            </Button> */}
            <Button onClick={() => document.getElementById("csv-input").click()}>
              อัพโหลด CSV ไฟล์
            </Button>
            <input
              id="csv-input"
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onClick={e => {
                // clear out the last value so onChange will fire next
                e.target.value = null;
              }}
              onChange={handleFileSelected}
            />

            <Button
              leftIcon={<FaTrashRestore />}
              variant="outline"
              colorScheme="orange"
              onClick={handleClear}
              isDisabled={!fileUploaded}
            >
              ล้างค่า
            </Button>
          </Flex>
          <Flex gap={2}>
            <Button
              px={12}
              leftIcon={<FaCalculator />}
              variant="outline"
              colorScheme="purple"
              onClick={openTargetModal}
              isDisabled={!fileUploaded}
              width="full"
            >
              เปรียบเทียบประสิทธิภาพ model
            </Button>
            <Button
              leftIcon={<FaBrain />}
              variant="outline"
              colorScheme="green"
              onClick={forecastOnOpen}
              isDisabled={!fileUploaded}
              width="full"
            >
              ทำนายผล
            </Button>
          </Flex>
        </Flex>
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />

        {forecastResults && (
          <>
            <ForecastCard
              selectedModel={forecastModel}
              forecastResults={forecastResults}
              selectedTarget={selectedTarget}
              columns={columns}
              keyMapping={keyMapping}
            />
            <Box
              mt={4}
              p={4}
              borderWidth="1px"
              borderColor="gray.800"
              borderRadius="md"
              boxShadow="md"
            >
              <Text mb={2} fontWeight="bold">
                ตัวแปรเป้าหมาย:{" "}
                {targetCurrentValue !== null ? selectedTarget : "N/A"}
              </Text>
              <Text mb={2}>
                เลือกเปอร์เซ็นต์ที่จะเปลี่ยนแปลง (1-20%) และทิศทาง:
              </Text>
              <Flex gap={4} align="center">
                <NumberInput
                  value={selectedAdjustmentPercent}
                  min={1}
                  max={20}
                  onChange={(valueString) =>
                    setSelectedAdjustmentPercent(parseInt(valueString, 10))
                  }
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <RadioGroup
                  onChange={setSelectedAdjustmentDirection}
                  value={selectedAdjustmentDirection}
                >
                  <Stack direction="row">
                    <Radio value="increase">เพิ่ม</Radio>
                    <Radio value="decrease">ลด</Radio>
                  </Stack>
                </RadioGroup>
              </Flex>
              <Text mt={4} mb={2} fontWeight="bold">
                เลือก Predictor ที่ต้องการคำนวณ:
              </Text>
              <RadioGroup
                onChange={setSelectedPredictorForAdjustment}
                value={selectedPredictorForAdjustment}
              >
                <Stack direction="column">
                  {forecastResults.impact_analysis &&
                    Object.keys(forecastResults.impact_analysis.impacts).map(
                      (p) => (
                        <Radio key={p} value={p}>
                          {p}
                        </Radio>
                      )
                    )}
                </Stack>
              </RadioGroup>
              <Button
                mt={4}
                colorScheme="blue"
                onClick={handleComputeSinglePredictorAdjustment}
                isDisabled={!selectedPredictorForAdjustment}
              >
                คำนวณ Predictor Adjustment
              </Button>
            </Box>
          </>
        )}

        {renderSinglePredictorAdjustmentResult()}

        {forecastResults ? (
          <Box mt={4} borderWidth="1px" borderRadius="md" p={4}>
            <Button
              variant="link"
              onClick={() => setShowDataGrid(!showDataGrid)}
            >
              {showDataGrid ? "ซ่อนข้อมูลจาก CSV" : "แสดงข้อมูลจาก CSV"}
            </Button>
            <Collapse in={showDataGrid}>
              <Box
                w="100%"
                overflow="auto"
                bg="white"
                height="600px"
                maxHeight="600px"
                mt={4}
              >
                {columns.length > 0 && rows.length > 0 ? (
                  <DataGrid
                    columns={columns}
                    rows={rows}
                    rowHeight={40}
                    defaultColumnOptions={{ resizable: true }}
                  />
                ) : (
                  <Text>กรุณาอัพโหลด CSV ไฟล์</Text>
                )}
              </Box>
            </Collapse>
          </Box>
        ) : (
          <Box
            w="100%"
            borderWidth="1px"
            borderRadius="md"
            p={4}
            overflow="auto"
            bg="white"
            height="600px"
            maxHeight="600px"
            mt={4}
          >
            {columns.length > 0 && rows.length > 0 ? (
              <DataGrid
                columns={columns}
                rows={rows}
                rowHeight={40}
                defaultColumnOptions={{ resizable: true }}
              />
            ) : (
              <Text>กรุณาอัพโหลด CSV ไฟล์</Text>
            )}
          </Box>
        )}
      </Box>

      <TargetSelectionModal
        isOpen={isTargetOpen}
        onClose={closeTargetModal}
        columns={columns}
        selectedTarget={selectedTarget}
        setSelectedTarget={setSelectedTarget}
        predictionMessage={predictionMessage}
        isPredictLoading={isPredictLoading}
        predictionResults={predictionResults}
        predictionOpen={predictionOpen}
        selectedSourceVariables={selectedSourceVariables}
        setSelectedSourceVariables={setSelectedSourceVariables}
        rePrediction={handleConfirmTarget}
      />

      <Modal isOpen={forecastIsOpen} onClose={forecastOnClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>เลือกตัวแปรและตั้งค่า forecast</ModalHeader>
          <ModalBody>
            <Text mb={4}>กรุณาเลือก target variable:</Text>
            <RadioGroup onChange={setSelectedTarget} value={selectedTarget}>
              <Stack direction="column">
                {columns.map((col) => (
                  <Radio key={col.key} value={col.key}>
                    {col.name}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
            <Box mt={4}>
              <Text mb={2}>จำนวนปีที่ทำนาย (forecast steps):</Text>
              <NumberInput
                value={forecastSteps}
                min={1}
                onChange={(valueString) => setForecastSteps(parseInt(valueString, 10))}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Box>
            <Box mt={4}>
              <Text mb={2}>เลือกโมเดลที่ใช้สำหรับการทำนาย:</Text>
              <RadioGroup onChange={setForecastModel} value={forecastModel}>
                <Stack direction="column">
                  <Radio value="arma">ARMA</Radio>
                  <Radio value="arima">ARIMA</Radio>
                  <Radio value="arimax">ARIMAX</Radio>
                  <Radio value="svr">SVR</Radio>
                  <Radio value="randomforest">RandomForestRegressor</Radio>
                  <Radio value="gradientboosting">GradientBoostingRegressor</Radio>
                </Stack>
              </RadioGroup>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={forecastOnClose}>
              ยกเลิก
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmForecast}
              isDisabled={!selectedTarget}
              isLoading={isForecastLoading}
            >
              ยืนยัน
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isSkipOpen} onClose={closeSkipModal} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            เลือกคอลัมน์ที่{" "}
            <Text as="span" color="red.500" fontWeight="bold">
              ไม่
            </Text>{" "}
            ต้องการนำเข้าข้อมูล
          </ModalHeader>
          <ModalBody>
            <Text mb={4}>คอลัมน์ที่สามารถนำเข้าข้อมูลได้ต้องเป็นตัวเลขเท่านั้น:</Text>
            <CheckboxGroup
              value={excludedCols}
              onChange={(vals) => setExcludedCols(vals)}
            >
              <Stack spacing={2}>
                {previewCols.map((col) => (
                  <Checkbox key={col} value={col}>
                    {col}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={closeSkipModal}>
              ยกเลิก
            </Button>
            <Button colorScheme="blue" onClick={handleModalConfirm}>
              ตกลง
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Flex>
  );
};

export default AnalyticModelChoosing;